import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListSubheader,
  Avatar,
  Chip
} from '@mui/material';
import {
  Home,
  People,
  Lock,
  Assignment,
  Build,
  Phone,
  Assessment,
  Group
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from '../theme/ThemeModeContext';
import { accent } from '../theme/tokens';
import { isOrdersEnabled } from '../config/features';
import { getManualPdfUrl } from '../lib/helpContext';

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const openHelp = (lang) => {
    handleMenuClose();
    window.open(getManualPdfUrl(lang), '_blank', 'noopener,noreferrer');
  };

  const navigationItems = [
    { label: 'Hjem', path: '/', icon: <Home /> },
    { label: 'Innbyggere', path: '/residents', icon: <People /> },
    { label: 'Låser', path: '/locks', icon: <Lock /> },
    { label: 'Tilordninger', path: '/assignments', icon: <Assignment /> },
    ...(isOrdersEnabled()
      ? [{ label: 'Bestillinger', path: '/orders', icon: <Build /> }]
      : []),
    { label: 'Telefoner', path: '/phones', icon: <Phone /> },
    { label: 'Tilgangsgrupper', path: '/access-groups', icon: <Group /> },
    { label: 'Rapporter', path: '/reports', icon: <Assessment /> }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <AppBar position="sticky" elevation={0} color="default">
      <Toolbar sx={{ py: 0.5 }}>
        <Typography
          variant="h6"
          component="div"
          onClick={() => navigate('/')}
          sx={{
            flexGrow: 1,
            fontWeight: 800,
            letterSpacing: '-0.5px',
            cursor: 'pointer',
            color: 'text.primary'
          }}
        >
          Open
          <Box component="span" sx={{ color: 'secondary.main' }}>
            Care
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, mr: 2, flexWrap: 'wrap' }}>
          {navigationItems.map((item) => (
            <Button
              key={item.path}
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                color: isActive(item.path) ? 'secondary.main' : 'text.secondary',
                fontWeight: isActive(item.path) ? 600 : 500,
                fontSize: '0.875rem',
                backgroundColor: isActive(item.path) ? 'var(--oc-accent-soft)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'var(--oc-ink-06)',
                  color: 'text.primary'
                },
                borderRadius: 100,
                px: 1.5,
                py: 0.75,
                minWidth: 'auto'
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            mr: 1,
            border: '1px solid var(--oc-ink-08)',
            borderRadius: 100,
            overflow: 'hidden',
            fontSize: '0.75rem',
            fontWeight: 600
          }}
          role="group"
          aria-label="Fargetema"
        >
          <Button
            size="small"
            onClick={() => setMode('light')}
            sx={{
              minWidth: 48,
              borderRadius: 0,
              py: 0.5,
              px: 1.5,
              color: mode === 'light' ? 'primary.contrastText' : 'text.secondary',
              bgcolor: mode === 'light' ? 'primary.main' : 'transparent',
              '&:hover': { bgcolor: mode === 'light' ? 'primary.dark' : 'var(--oc-ink-06)' }
            }}
          >
            Lys
          </Button>
          <Button
            size="small"
            onClick={() => setMode('dark')}
            sx={{
              minWidth: 48,
              borderRadius: 0,
              py: 0.5,
              px: 1.5,
              color: mode === 'dark' ? 'primary.contrastText' : 'text.secondary',
              bgcolor: mode === 'dark' ? 'primary.main' : 'transparent',
              '&:hover': { bgcolor: mode === 'dark' ? 'primary.dark' : 'var(--oc-ink-06)' }
            }}
          >
            Mørk
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={user?.roles?.[0] || 'Staff'}
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'var(--oc-border)',
              color: 'text.secondary',
              fontWeight: 600
            }}
          />

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            onClick={handleProfileMenuOpen}
            sx={{ color: 'text.primary' }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: accent.main, fontWeight: 700 }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem disabled>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {user?.name || 'Demo User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email || 'demo@opencare.no'}
              </Typography>
            </Box>
          </MenuItem>
          <ListSubheader sx={{ lineHeight: 2.2 }}>Brukermanual</ListSubheader>
          <MenuItem onClick={() => openHelp('nb')}>Norsk</MenuItem>
          <MenuItem onClick={() => openHelp('en')}>English</MenuItem>
          <MenuItem onClick={handleLogout}>
            Logg ut
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
